import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "Asistencia";

/**
 * Normaliza textos para comparar nombres de módulos.
 */
const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

/**
 * Normaliza el UID para que siempre tenga el mismo formato.
 *
 * Ejemplos:
 * 04:A1:B2:C3:D4 -> 04A1B2C3D4
 * 04-a1-b2-c3-d4 -> 04A1B2C3D4
 */
const normalizarUID = (valor = "") =>
  String(valor)
    .trim()
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

/**
 * Configuración de autenticación para Axios.
 */
const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
};

/**
 * Obtiene los permisos del módulo de asistencia.
 */
const obtenerModuloAsistencia = () => {
  try {
    const permisosStorage = localStorage.getItem("permisos");
    const usuarioStorage = localStorage.getItem("usuario");
    const userStorage = localStorage.getItem("user");
    const usuariosStorage = localStorage.getItem("usuarios");

    const permisosData = permisosStorage
      ? JSON.parse(permisosStorage)
      : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
          ? JSON.parse(userStorage)
          : usuariosStorage
            ? JSON.parse(usuariosStorage)
            : {};

    const modulos = Array.isArray(permisosData)
      ? permisosData
      : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

    const moduloBuscado = normalizarTexto(MODULO);

    return (
      modulos.find((modulo) => {
        const nombre =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.nombre ||
          "";

        return normalizarTexto(nombre) === moduloBuscado;
      }) || {}
    );
  } catch (error) {
    console.error("Error leyendo los permisos:", error);
    return {};
  }
};

/**
 * Muestra una notificación pequeña para no interrumpir
 * la lectura continua de varias tarjetas.
 */
const mostrarToast = ({
  icon = "success",
  title,
  text,
  timer = 1800,
}) => {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon,
    title,
    text,
    showConfirmButton: false,
    timer,
    timerProgressBar: true,
  });
};


/**
 * Imagen predeterminada para aprendices sin fotografía.
 * Se genera como SVG local para no depender de servicios externos.
 */
const FOTO_APRENDIZ_DEFAULT = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300">
    <rect width="300" height="300" fill="#f1f3f5"/>
    <circle cx="150" cy="105" r="58" fill="#adb5bd"/>
    <path d="M45 290c10-72 50-108 105-108s95 36 105 108" fill="#adb5bd"/>
  </svg>
`)}`;

/**
 * Evita insertar contenido HTML sin escapar dentro de SweetAlert.
 */
const escaparHTML = (valor = "") =>
  String(valor)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

/**
 * Obtiene la fotografía usando directamente el campo imgAprendiz.
 * El backend ya entrega una URL válida, por lo que no se construyen
 * rutas adicionales desde este componente.
 */
const obtenerFotoAprendiz = (...valores) => {
  for (const valor of valores) {
    if (!valor) continue;

    if (typeof valor === "string" && valor.trim()) {
      return valor.trim();
    }

    if (typeof valor === "object") {
      const imagen =
        valor.imgAprendiz ||
        valor.fotoAprendiz ||
        valor.imagenAprendiz ||
        valor.foto ||
        valor.imagen ||
        valor.fotografia ||
        "";

      if (typeof imagen === "string" && imagen.trim()) {
        return imagen.trim();
      }
    }
  }

  return FOTO_APRENDIZ_DEFAULT;
};

/**
 * Muestra los datos del aprendiz después de leer su tarjeta.
 */
const mostrarAprendizSweetAlert = async ({
  icon = "success",
  titulo = "Asistencia registrada",
  mensaje = "El aprendiz fue marcado como presente.",
  nombreCompleto = "Aprendiz",
  documentoAprendiz = "No disponible",
  fichaGrupo = "No disponible",
  estadoMatricula = "No disponible",
  tipoMatricula = "No disponible",
  fotoAprendiz,
  timer = 2800,
}) => {
  const foto = obtenerFotoAprendiz(fotoAprendiz);

  return Swal.fire({
    icon,
    title: titulo,
    imageUrl: foto,
    imageAlt: `Foto de ${nombreCompleto}`,
    imageWidth: 145,
    imageHeight: 145,
    html: `
      <div class="text-start">
        <h5 class="text-center fw-bold mb-3">
          ${escaparHTML(nombreCompleto)}
        </h5>

        <div class="border rounded-3 overflow-hidden">
          <div class="d-flex justify-content-between gap-3 px-3 py-2 border-bottom">
            <span class="text-secondary">Documento</span>
            <strong>${escaparHTML(documentoAprendiz)}</strong>
          </div>

          <div class="d-flex justify-content-between gap-3 px-3 py-2 border-bottom">
            <span class="text-secondary">Ficha</span>
            <strong>${escaparHTML(fichaGrupo)}</strong>
          </div>

          <div class="d-flex justify-content-between gap-3 px-3 py-2 border-bottom">
            <span class="text-secondary">Estado</span>
            <strong>${escaparHTML(estadoMatricula)}</strong>
          </div>

          <div class="d-flex justify-content-between gap-3 px-3 py-2">
            <span class="text-secondary">Tipo de matrícula</span>
            <strong>${escaparHTML(tipoMatricula)}</strong>
          </div>
        </div>

        <div class="alert alert-light border text-center mt-3 mb-0 py-2">
          ${escaparHTML(mensaje)}
        </div>
      </div>
    `,
    showConfirmButton: false,
    timer,
    timerProgressBar: true,
    allowOutsideClick: false,
    allowEscapeKey: false,
    customClass: {
      popup: "rounded-4",
      image: "rounded-circle border shadow-sm object-fit-cover",
    },
    didOpen: () => {
      const imagen = Swal.getPopup()?.querySelector(
        ".swal2-image"
      );

      if (imagen) {
        imagen.onerror = () => {
          imagen.onerror = null;
          imagen.src = FOTO_APRENDIZ_DEFAULT;
        };
      }
    },
  });
};

const RegistrarAsistencia = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  /*
   * Datos de la actividad.
   */
  const [nombreActividad, setNombreActividad] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [fecha, setFecha] = useState("");
  const [matriculaId, setMatriculaId] = useState("");

  /*
   * Aprendices y asistencias.
   */
  const [aprendices, setAprendices] = useState([]);
  const [asistencias, setAsistencias] = useState({});
  const [tieneAsistencias, setTieneAsistencias] =
    useState(false);

  /*
   * UID y NFC.
   */
  const [tarjetaUID, setTarjetaUID] = useState("");
  const [leyendoNFC, setLeyendoNFC] = useState(false);
  const [procesandoUID, setProcesandoUID] = useState(false);

  /*
   * Estados de carga.
   */
  const [cargandoActividad, setCargandoActividad] =
    useState(false);
  const [cargandoAprendices, setCargandoAprendices] =
    useState(false);
  const [cargandoAsistencias, setCargandoAsistencias] =
    useState(false);
  const [saving, setSaving] = useState(false);

  /*
   * Referencias.
   */
  const uidInputRef = useRef(null);
  const lectorNFCRef = useRef(null);
  const abortControllerRef = useRef(null);
  const uidEnProcesoRef = useRef(false);
  const ultimoUIDRef = useRef({
    uid: "",
    fecha: 0,
  });

  /*
   * Permite que el evento NFC use siempre la versión
   * más reciente de procesarUID.
   */
  const procesarUIDRef = useRef(null);

  const moduloAsistencia = useMemo(
    () => obtenerModuloAsistencia(),
    []
  );

  const puedeCrear =
    moduloAsistencia?.crear === true ||
    moduloAsistencia?.puedeCrear === true ||
    moduloAsistencia?.create === true;

  const puedeActualizar =
    moduloAsistencia?.actualizar === true ||
    moduloAsistencia?.editar === true ||
    moduloAsistencia?.puedeActualizar === true ||
    moduloAsistencia?.update === true;

  const nfcDisponible = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      "NDEFReader" in window
    );
  }, []);

  const contextoSeguro = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      window.isSecureContext
    );
  }, []);

  const loading =
    cargandoActividad ||
    cargandoAprendices ||
    cargandoAsistencias;

  const disabledAll = loading || saving;

  const puedeModificarFormulario = tieneAsistencias
    ? puedeActualizar
    : puedeCrear;

  const escaneoDisponible =
    puedeCrear &&
    !tieneAsistencias &&
    !loading &&
    !saving &&
    aprendices.length > 0;

  /**
   * Maneja una sesión expirada.
   */
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status !== 401) {
        return false;
      }

      Swal.fire({
        icon: "warning",
        title: "Sesión expirada",
        text: "Por favor inicia sesión nuevamente.",
        confirmButtonText: "Aceptar",
      }).then(() => {
        localStorage.removeItem("token");
        navigate("/login");
      });

      return true;
    },
    [navigate]
  );

  /**
   * Detiene la lectura NFC.
   */
  const detenerLecturaNFC = useCallback(() => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    } catch (error) {
      console.error("Error deteniendo NFC:", error);
    } finally {
      abortControllerRef.current = null;
      lectorNFCRef.current = null;
      setLeyendoNFC(false);
    }
  }, []);

  /**
   * Regresa a la pantalla anterior.
   */
  const regresar = useCallback(() => {
    detenerLecturaNFC();
    navigate(-1);
  }, [detenerLecturaNFC, navigate]);

  /**
   * Detiene NFC cuando se desmonta el componente.
   */
  useEffect(() => {
    return () => {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      } catch (error) {
        console.error(
          "Error cerrando el lector NFC:",
          error
        );
      }
    };
  }, []);

  /**
   * Si ya existen asistencias guardadas, detiene NFC.
   */
  useEffect(() => {
    if (tieneAsistencias) {
      detenerLecturaNFC();
    }
  }, [detenerLecturaNFC, tieneAsistencias]);

  /**
   * Obtiene la actividad.
   */
  useEffect(() => {
    const obtenerActividad = async () => {
      try {
        setCargandoActividad(true);

        const response = await axios.get(
          `/api/actividad/listid/${id}`,
          authConfig()
        );

        const datos = response.data || {};

        setNombreActividad(
          datos.nombreActividad || ""
        );

        setDescripcion(datos.descripcion || "");

        setFecha(
          datos.fecha
            ? String(datos.fecha).substring(0, 10)
            : ""
        );

        setMatriculaId(
          datos.matricula?._id ||
            datos.matricula ||
            ""
        );
      } catch (error) {
        console.error(
          "Error obteniendo la actividad:",
          error
        );

        if (manejarErrorSesion(error)) {
          return;
        }

        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar la actividad.",
        });
      } finally {
        setCargandoActividad(false);
      }
    };

    if (id) {
      obtenerActividad();
    }
  }, [id, manejarErrorSesion]);

  /**
   * Obtiene los aprendices de la matrícula.
   */
  useEffect(() => {
    const obtenerAprendices = async () => {
      if (!matriculaId) {
        return;
      }

      try {
        setCargandoAprendices(true);

        const response = await axios.get(
          `/api/detallematricula/listbyid/${matriculaId}`,
          authConfig()
        );

        const datos = response.data || {};
        const detalles = Array.isArray(datos?.detalles)
          ? datos.detalles
          : [];

        setAprendices(detalles);

        const asistenciasIniciales = {};

        detalles.forEach((aprendiz) => {
          asistenciasIniciales[aprendiz._id] = {
            asistio: false,
            justificacion: "",
          };
        });

        setAsistencias(asistenciasIniciales);
      } catch (error) {
        console.error(
          "Error obteniendo los aprendices:",
          error
        );

        if (manejarErrorSesion(error)) {
          return;
        }

        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar la lista de aprendices.",
        });
      } finally {
        setCargandoAprendices(false);
      }
    };

    obtenerAprendices();
  }, [manejarErrorSesion, matriculaId]);

  /**
   * Obtiene asistencias previamente registradas.
   */
  useEffect(() => {
    const obtenerAsistencias = async () => {
      try {
        setCargandoAsistencias(true);

        const response = await axios.get(
          `/api/asistencia/actividad/${id}`,
          authConfig()
        );

        const asistenciasExistentes = Array.isArray(
          response.data
        )
          ? response.data
          : [];

        if (asistenciasExistentes.length === 0) {
          setTieneAsistencias(false);
          return;
        }

        const nuevoEstado = {};

        asistenciasExistentes.forEach((asistencia) => {
          const detalleId =
            asistencia.detalleMatricula?._id ||
            asistencia.detalleMatricula;

          if (!detalleId) {
            return;
          }

          nuevoEstado[detalleId] = {
            id: asistencia._id,
            asistio: Boolean(asistencia.asistio),
            justificacion:
              asistencia.justificacion || "",
          };
        });

        setAsistencias((estadoAnterior) => ({
          ...estadoAnterior,
          ...nuevoEstado,
        }));

        setTieneAsistencias(true);
      } catch (error) {
        if (manejarErrorSesion(error)) {
          return;
        }

        /*
         * Si el endpoint responde 404 porque todavía no hay
         * asistencias, se considera una actividad nueva.
         */
        if (error?.response?.status === 404) {
          setTieneAsistencias(false);
          return;
        }

        console.warn(
          "No se pudieron cargar asistencias previas:",
          error
        );

        setTieneAsistencias(false);
      } finally {
        setCargandoAsistencias(false);
      }
    };

    if (aprendices.length > 0 && id) {
      obtenerAsistencias();
    }
  }, [
    aprendices.length,
    id,
    manejarErrorSesion,
  ]);

  /**
   * Marca o desmarca manualmente la asistencia.
   */
  const handleCheckboxChange = (idAprendiz) => {
    if (
      tieneAsistencias ||
      !puedeCrear ||
      disabledAll
    ) {
      return;
    }

    setAsistencias((estadoAnterior) => ({
      ...estadoAnterior,
      [idAprendiz]: {
        ...estadoAnterior[idAprendiz],
        asistio:
          !estadoAnterior[idAprendiz]?.asistio,
      },
    }));
  };

  /**
   * Actualiza una observación o justificación.
   */
  const handleJustificacionChange = (
    idAprendiz,
    value
  ) => {
    if (!puedeModificarFormulario) {
      return;
    }

    setAsistencias((estadoAnterior) => ({
      ...estadoAnterior,
      [idAprendiz]: {
        ...estadoAnterior[idAprendiz],
        justificacion: value,
      },
    }));
  };

  /**
   * Procesa un UID proveniente de:
   *
   * - NFC del celular.
   * - Lector RFID externo.
   * - Escritura manual.
   */
  const procesarUID = useCallback(
    async (uidRecibido) => {
      const uidNormalizado =
        normalizarUID(uidRecibido);

      if (!puedeCrear) {
        await Swal.fire({
          icon: "warning",
          title: "Acceso denegado",
          text: "No tienes permisos para registrar asistencias.",
        });

        return;
      }

      if (tieneAsistencias) {
        await Swal.fire({
          icon: "info",
          title: "Asistencia ya registrada",
          text:
            "La asistencia de esta actividad ya fue guardada. En este momento solo puedes actualizar las observaciones.",
        });

        return;
      }

      if (!uidNormalizado) {
        mostrarToast({
          icon: "warning",
          title: "UID inválido",
          text: "No se recibió un UID válido.",
        });

        return;
      }

      if (aprendices.length === 0) {
        mostrarToast({
          icon: "warning",
          title: "Sin aprendices",
          text:
            "La actividad no tiene aprendices cargados.",
        });

        return;
      }

      /*
       * Evita procesar dos lecturas simultáneas.
       */
      if (uidEnProcesoRef.current) {
        return;
      }

      const ahora = Date.now();
      const ultimaLectura = ultimoUIDRef.current;

      /*
       * Evita que una tarjeta mantenida sobre el celular
       * registre varias lecturas consecutivas.
       */
      if (
        ultimaLectura.uid === uidNormalizado &&
        ahora - ultimaLectura.fecha < 2500
      ) {
        return;
      }

      ultimoUIDRef.current = {
        uid: uidNormalizado,
        fecha: ahora,
      };

      uidEnProcesoRef.current = true;
      setProcesandoUID(true);
      setTarjetaUID(uidNormalizado);

      try {
        const response = await axios.post(
          "/api/tarjetas/buscar-detalle-matricula",
          {
            tarjetaUID: uidNormalizado,
          },
          authConfig()
        );

        const detalleMatriculaId =
          response.data?.detalleMatriculaId;

        const nombreCompleto =
          response.data?.nombreCompleto ||
          "Aprendiz";

        if (!detalleMatriculaId) {
          throw new Error(
            "El servidor no devolvió el detalle de matrícula."
          );
        }

        /*
         * Comprueba que el aprendiz asociado con la tarjeta
         * pertenezca a la matrícula de esta actividad.
         */
        const aprendizEnActividad = aprendices.find(
          (aprendiz) =>
            String(aprendiz._id) ===
            String(detalleMatriculaId)
        );

        const aprendizBase =
          aprendizEnActividad?.aprendizObjectId ||
          aprendizEnActividad?.aprendiz ||
          response.data?.aprendiz ||
          {};

        const nombreDesdeModelo = [
          aprendizBase?.nombre1Aprendiz,
          aprendizBase?.nombre2Aprendiz,
          aprendizBase?.apellido1Aprendiz,
          aprendizBase?.apellido2Aprendiz,
        ]
          .filter(Boolean)
          .join(" ")
          .trim()
          .replace(/\s+/g, " ");

        const informacionAprendiz = {
          nombreCompleto:
            response.data?.nombreCompleto ||
            aprendizEnActividad?.nombreAprendiz ||
            nombreDesdeModelo ||
            nombreCompleto,

          documentoAprendiz:
            response.data?.documentoAprendiz ??
            aprendizEnActividad?.documentoAprendiz ??
            aprendizBase?.documentoAprendiz ??
            "No disponible",

          fichaGrupo:
            response.data?.fichaGrupo ??
            aprendizEnActividad?.fichaGrupo ??
            "No disponible",

          estadoMatricula:
            response.data?.estadoMatricula ??
            aprendizEnActividad?.estadoMatricula ??
            "No disponible",

          tipoMatricula:
            response.data?.tipoMatricula ??
            aprendizEnActividad?.tipoMatricula ??
            "No disponible",

          fotoAprendiz:
            aprendizBase?.imgAprendiz ||
            aprendizEnActividad?.imgAprendiz ||
            response.data?.aprendiz?.imgAprendiz ||
            response.data?.imgAprendiz ||
            aprendizBase?.fotoAprendiz ||
            aprendizEnActividad?.fotoAprendiz ||
            response.data?.fotoAprendiz ||
            "",
        };

        if (!aprendizEnActividad) {
          await mostrarAprendizSweetAlert({
            icon: "warning",
            titulo: "Aprendiz fuera del grupo",
            mensaje:
              "La tarjeta pertenece a un aprendiz que no está matriculado en esta actividad.",
            ...informacionAprendiz,
            timer: 3200,
          });

          return;
        }

        /*
         * Evita informar una asistencia nueva cuando el
         * aprendiz ya estaba marcado.
         */
        if (
          asistencias[detalleMatriculaId]?.asistio
        ) {
          await mostrarAprendizSweetAlert({
            icon: "info",
            titulo: "Asistencia ya registrada",
            mensaje:
              "El aprendiz ya se encuentra marcado como presente.",
            ...informacionAprendiz,
            timer: 2600,
          });

          return;
        }

        setAsistencias((estadoAnterior) => ({
          ...estadoAnterior,
          [detalleMatriculaId]: {
            ...(estadoAnterior[
              detalleMatriculaId
            ] || {}),
            asistio: true,
          },
        }));

        await mostrarAprendizSweetAlert({
          icon: "success",
          titulo: "Asistencia registrada",
          mensaje:
            "El aprendiz fue marcado correctamente como presente.",
          ...informacionAprendiz,
          timer: 2800,
        });
      } catch (error) {
        console.error(
          "Error procesando el UID:",
          error
        );

        if (manejarErrorSesion(error)) {
          return;
        }

        mostrarToast({
          icon: "error",
          title: "Tarjeta no reconocida",
          text:
            error.response?.data?.message ||
            error.message ||
            "No se pudo procesar la tarjeta.",
          timer: 2600,
        });
      } finally {
        uidEnProcesoRef.current = false;
        setProcesandoUID(false);
        setTarjetaUID("");

        /*
         * Conserva el foco para lectores RFID externos
         * que funcionan como teclado.
         */
        setTimeout(() => {
          uidInputRef.current?.focus();
        }, 100);
      }
    },
    [
      aprendices,
      asistencias,
      manejarErrorSesion,
      puedeCrear,
      tieneAsistencias,
    ]
  );

  /**
   * Mantiene actualizada la función usada por el evento NFC.
   */
  useEffect(() => {
    procesarUIDRef.current = procesarUID;
  }, [procesarUID]);

  /**
   * Inicia una lectura NFC continua.
   *
   * No se detiene después de la primera tarjeta, porque
   * la finalidad es registrar varios aprendices.
   */
  const iniciarLecturaNFC = useCallback(async () => {
    if (!puedeCrear) {
      await Swal.fire({
        icon: "warning",
        title: "Acceso denegado",
        text: "No tienes permisos para registrar asistencias.",
      });

      return;
    }

    if (tieneAsistencias) {
      await Swal.fire({
        icon: "info",
        title: "Asistencia ya guardada",
        text:
          "La lectura de tarjetas está disponible antes de guardar la asistencia.",
      });

      return;
    }

    if (aprendices.length === 0) {
      await Swal.fire({
        icon: "warning",
        title: "Sin aprendices",
        text:
          "Espera a que se cargue la lista de aprendices.",
      });

      return;
    }

    if (!nfcDisponible) {
      await Swal.fire({
        icon: "warning",
        title: "NFC no disponible",
        text:
          "Este navegador no permite Web NFC. Usa Google Chrome en un celular Android compatible o utiliza un lector RFID externo.",
      });

      return;
    }

    if (!contextoSeguro) {
      await Swal.fire({
        icon: "warning",
        title: "Se requiere HTTPS",
        text:
          "La lectura NFC requiere que la aplicación se abra desde una dirección HTTPS.",
      });

      return;
    }

    try {
      detenerLecturaNFC();

      const abortController =
        new AbortController();

      const lector =
        new window.NDEFReader();

      abortControllerRef.current =
        abortController;

      lectorNFCRef.current = lector;

      lector.addEventListener(
        "reading",
        (evento) => {
          const uidNormalizado =
            normalizarUID(
              evento.serialNumber || ""
            );

          if (!uidNormalizado) {
            mostrarToast({
              icon: "warning",
              title: "UID no disponible",
              text:
                "La tarjeta fue detectada, pero el navegador no entregó su número de serie.",
              timer: 2600,
            });

            return;
          }

          procesarUIDRef.current?.(
            uidNormalizado
          );
        }
      );

      lector.addEventListener(
        "readingerror",
        () => {
          mostrarToast({
            icon: "error",
            title: "Error de lectura NFC",
            text:
              "La tarjeta fue detectada, pero no pudo ser procesada.",
            timer: 2500,
          });
        }
      );

      await lector.scan({
        signal: abortController.signal,
      });

      setLeyendoNFC(true);

      mostrarToast({
        icon: "info",
        title: "Lector NFC activo",
        text:
          "Acerca las tarjetas a la parte posterior del celular.",
        timer: 2400,
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }

      console.error(
        "Error iniciando NFC:",
        error
      );

      detenerLecturaNFC();

      let mensaje =
        "No se pudo iniciar el lector NFC.";

      switch (error?.name) {
        case "NotAllowedError":
          mensaje =
            "El permiso NFC fue rechazado. Autoriza el acceso NFC desde Chrome.";
          break;

        case "NotSupportedError":
          mensaje =
            "El dispositivo o el navegador no permite esta lectura NFC.";
          break;

        case "NotReadableError":
          mensaje =
            "El NFC está ocupado por otra aplicación o no se encuentra disponible.";
          break;

        case "InvalidStateError":
          mensaje =
            "Ya existe una lectura NFC activa.";
          break;

        case "SecurityError":
          mensaje =
            "La lectura NFC fue bloqueada. Verifica que la aplicación use HTTPS.";
          break;

        default:
          mensaje =
            error?.message ||
            "No se pudo iniciar el lector NFC.";
          break;
      }

      await Swal.fire({
        icon: "error",
        title: "Error NFC",
        text: mensaje,
      });
    }
  }, [
    aprendices.length,
    contextoSeguro,
    detenerLecturaNFC,
    nfcDisponible,
    puedeCrear,
    tieneAsistencias,
  ]);

  /**
   * Procesa el UID cuando el lector externo presiona Enter.
   */
  const handleUIDKeyDown = async (evento) => {
    if (evento.key !== "Enter") {
      return;
    }

    evento.preventDefault();

    await procesarUID(tarjetaUID);
  };

  /**
   * Procesa manualmente el contenido del campo UID.
   */
  const handleProcesarUID = async () => {
    await procesarUID(tarjetaUID);
  };

  /**
   * Actualiza manualmente el campo UID.
   */
  const handleTarjetaUIDChange = (evento) => {
    setTarjetaUID(
      normalizarUID(evento.target.value)
    );
  };

  /**
   * Guarda todas las asistencias por primera vez.
   */
  const saveAsistencia = async () => {
    if (!puedeCrear) {
      await Swal.fire({
        icon: "warning",
        title: "Acceso denegado",
        text:
          "No tienes permisos para registrar asistencias.",
      });

      return;
    }

    try {
      detenerLecturaNFC();
      setSaving(true);

      const registros = aprendices.map(
        (aprendiz) => {
          const datos =
            asistencias[aprendiz._id] || {};

          return {
            actividad: id,
            detalleMatricula: aprendiz._id,
            asistio: Boolean(datos.asistio),
            justificacion:
              datos.justificacion || "",
          };
        }
      );

      await axios.post(
        "/api/asistencia/addmany",
        registros,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "Asistencias registradas",
        text:
          "Las asistencias se guardaron correctamente.",
        timer: 1700,
        showConfirmButton: false,
      });

      navigate(-1);
    } catch (error) {
      console.error(
        "Error guardando la asistencia:",
        error
      );

      if (manejarErrorSesion(error)) {
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text:
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Ocurrió un error al registrar las asistencias.",
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Actualiza las observaciones de asistencias existentes.
   */
  const updateAsistencia = async () => {
    if (!puedeActualizar) {
      await Swal.fire({
        icon: "warning",
        title: "Acceso denegado",
        text:
          "No tienes permisos para actualizar asistencias.",
      });

      return;
    }

    try {
      detenerLecturaNFC();
      setSaving(true);

      const registros = aprendices
        .map((aprendiz) => {
          const datos =
            asistencias[aprendiz._id] || {};

          return {
            id: datos.id,
            justificacion:
              datos.justificacion || "",
          };
        })
        .filter((registro) => registro.id);

      const response = await axios.put(
        "/api/asistencia/updatemany",
        registros,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "Asistencias actualizadas",
        text:
          `${
            response.data?.actualizados ||
            registros.length
          } registros actualizados correctamente.`,
        timer: 1700,
        showConfirmButton: false,
      });

      navigate(-1);
    } catch (error) {
      console.error(
        "Error actualizando la asistencia:",
        error
      );

      if (manejarErrorSesion(error)) {
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text:
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Ocurrió un error al actualizar las asistencias.",
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Envía el formulario.
   */
  const actions = async (evento) => {
    evento.preventDefault();

    if (tieneAsistencias) {
      await updateAsistencia();
    } else {
      await saveAsistencia();
    }
  };

  const totalPresentes = useMemo(() => {
    return Object.values(
      asistencias || {}
    ).filter((item) => item?.asistio).length;
  }, [asistencias]);

  const totalAprendices =
    aprendices?.length || 0;

  /**
   * Conserva el foco para lectores externos.
   */
  useEffect(() => {
    if (
      !disabledAll &&
      !procesandoUID &&
      !leyendoNFC &&
      !tieneAsistencias
    ) {
      uidInputRef.current?.focus();
    }
  }, [
    disabledAll,
    leyendoNFC,
    procesandoUID,
    tieneAsistencias,
  ]);

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Encabezado */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {tieneAsistencias
                    ? "Editar Asistencia"
                    : "Registrar Asistencia"}
                </h5>

                <small className="text-secondary">
                  Lee las tarjetas mediante NFC,
                  lector externo o marca la
                  asistencia manualmente.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={regresar}
                  disabled={disabledAll}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2" />
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    uidInputRef.current?.focus()
                  }
                  disabled={
                    !escaneoDisponible ||
                    leyendoNFC
                  }
                  title="Enfocar campo UID"
                >
                  <i className="fa-solid fa-keyboard me-2" />
                  Lector externo
                </button>

                <button
                  type="button"
                  className={
                    leyendoNFC
                      ? "btn btn-outline-danger"
                      : "btn btn-outline-primary"
                  }
                  onClick={
                    leyendoNFC
                      ? detenerLecturaNFC
                      : iniciarLecturaNFC
                  }
                  disabled={
                    !leyendoNFC &&
                    !escaneoDisponible
                  }
                  title={
                    leyendoNFC
                      ? "Detener lectura NFC"
                      : "Iniciar lectura NFC"
                  }
                >
                  <i
                    className={`fa-solid ${
                      leyendoNFC
                        ? "fa-circle-stop"
                        : "fa-wifi"
                    } me-2`}
                  />

                  {leyendoNFC
                    ? "Detener NFC"
                    : "Leer NFC"}
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {!puedeModificarFormulario && (
              <div className="alert alert-warning mb-4">
                <i className="fa-solid fa-triangle-exclamation me-2" />
                No tienes permisos para{" "}
                {tieneAsistencias
                  ? "actualizar"
                  : "registrar"}{" "}
                asistencias.
              </div>
            )}

            {!nfcDisponible && (
              <div className="alert alert-info mb-4">
                <i className="fa-solid fa-circle-info me-2" />

                <strong>
                  NFC no disponible en este navegador.
                </strong>

                <div className="mt-1">
                  Puedes utilizar un lector RFID
                  externo o ingresar el UID
                  manualmente.
                </div>
              </div>
            )}

            {nfcDisponible &&
              !contextoSeguro && (
                <div className="alert alert-warning mb-4">
                  <i className="fa-solid fa-lock me-2" />
                  La lectura NFC requiere que la
                  aplicación se abra mediante HTTPS.
                </div>
              )}

            {tieneAsistencias && (
              <div className="alert alert-secondary mb-4">
                <i className="fa-solid fa-circle-info me-2" />
                La asistencia ya fue guardada. Puedes
                actualizar las observaciones, pero no
                registrar nuevas lecturas de tarjetas.
              </div>
            )}

            {leyendoNFC && (
              <div className="alert alert-primary mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <i className="fa-solid fa-wifi me-2" />

                    <strong>
                      Lector NFC activo.
                    </strong>

                    <div className="mt-1">
                      Acerca cada tarjeta a la parte
                      posterior del celular. El lector
                      continuará activo después de cada
                      registro.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={detenerLecturaNFC}
                  >
                    <i className="fa-solid fa-circle-stop me-2" />
                    Detener
                  </button>
                </div>
              </div>
            )}

            {(loading || saving) && (
              <div className="text-center text-secondary py-3">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                />

                <div className="mt-2">
                  {saving
                    ? "Guardando..."
                    : "Cargando información..."}
                </div>
              </div>
            )}

            <form onSubmit={actions}>
              <div className="row g-3">
                {/* Panel izquierdo */}
                <div className="col-12 col-md-4">
                  <div className="border rounded-4 p-3 p-md-4 bg-body-tertiary">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <div className="fw-semibold">
                          Actividad
                        </div>

                        <div className="text-secondary small">
                          {totalPresentes}/
                          {totalAprendices} presentes
                        </div>
                      </div>

                      <div className="text-end">
                        <span className="badge text-bg-dark">
                          <i className="fa-solid fa-calendar-day me-1" />
                          {fecha || "—"}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label
                        htmlFor="nombreActividad"
                        className="form-label fw-semibold"
                      >
                        Nombre
                      </label>

                      <input
                        id="nombreActividad"
                        type="text"
                        className="form-control"
                        value={nombreActividad}
                        readOnly
                      />
                    </div>

                    <div className="mb-3">
                      <label
                        htmlFor="descripcionActividad"
                        className="form-label fw-semibold"
                      >
                        Descripción
                      </label>

                      <textarea
                        id="descripcionActividad"
                        className="form-control"
                        value={descripcion}
                        rows={3}
                        readOnly
                      />
                    </div>

                    {/* Lector UID */}
                    <div className="border rounded-4 bg-white p-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="fw-semibold">
                          <i className="fa-solid fa-id-card me-2 text-secondary" />
                          Leer Tarjeta UID
                        </div>

                        <span
                          className={`badge ${
                            leyendoNFC
                              ? "text-bg-primary"
                              : "text-bg-secondary"
                          }`}
                        >
                          {leyendoNFC
                            ? "NFC activo"
                            : "Enter"}
                        </span>
                      </div>

                      <div className="input-group">
                        <input
                          ref={uidInputRef}
                          type="text"
                          autoComplete="off"
                          className="form-control text-uppercase"
                          placeholder="UID de la tarjeta"
                          value={tarjetaUID}
                          onChange={
                            handleTarjetaUIDChange
                          }
                          onKeyDown={
                            handleUIDKeyDown
                          }
                          disabled={
                            !escaneoDisponible ||
                            leyendoNFC ||
                            procesandoUID
                          }
                        />

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleProcesarUID}
                          disabled={
                            !escaneoDisponible ||
                            leyendoNFC ||
                            procesandoUID ||
                            !tarjetaUID
                          }
                          title="Procesar UID"
                        >
                          <i
                            className={`fa-solid ${
                              procesandoUID
                                ? "fa-spinner fa-spin"
                                : "fa-check"
                            }`}
                          />
                        </button>
                      </div>

                      <div className="d-grid gap-2 mt-3">
                        <button
                          type="button"
                          className={
                            leyendoNFC
                              ? "btn btn-outline-danger"
                              : "btn btn-outline-primary"
                          }
                          onClick={
                            leyendoNFC
                              ? detenerLecturaNFC
                              : iniciarLecturaNFC
                          }
                          disabled={
                            !leyendoNFC &&
                            !escaneoDisponible
                          }
                        >
                          <i
                            className={`fa-solid ${
                              leyendoNFC
                                ? "fa-circle-stop"
                                : "fa-wifi"
                            } me-2`}
                          />

                          {leyendoNFC
                            ? "Detener lectura NFC"
                            : "Iniciar lectura NFC"}
                        </button>
                      </div>

                      <div className="form-text mt-2">
                        {leyendoNFC
                          ? "Acerca las tarjetas al celular una por una."
                          : "Usa NFC, escribe el UID o escanea con un lector externo."}
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="d-grid mt-3 gap-2">
                      <button
                        type="submit"
                        className={`btn ${
                          tieneAsistencias
                            ? "btn-outline-secondary"
                            : "btn-dark"
                        }`}
                        disabled={
                          disabledAll ||
                          procesandoUID ||
                          !puedeModificarFormulario
                        }
                        title={
                          tieneAsistencias
                            ? "Actualizar asistencias"
                            : "Guardar asistencias"
                        }
                      >
                        <i
                          className={`fa-solid ${
                            saving
                              ? "fa-spinner fa-spin"
                              : tieneAsistencias
                                ? "fa-rotate"
                                : "fa-floppy-disk"
                          } me-2`}
                        />

                        {saving
                          ? "Guardando..."
                          : tieneAsistencias
                            ? "Actualizar"
                            : "Guardar"}
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={regresar}
                        disabled={disabledAll}
                        title="Cancelar"
                      >
                        <i className="fa-solid fa-xmark me-2" />
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tabla de escritorio */}
                <div className="col-12 col-md-8">
                  <div className="d-none d-md-block">
                    <div className="border rounded-4 overflow-hidden">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th>Aprendiz</th>

                            <th
                              className="text-center"
                              style={{
                                width: 150,
                              }}
                            >
                              Asistencia
                            </th>

                            <th>
                              Observaciones
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {aprendices.length >
                          0 ? (
                            aprendices.map(
                              (item) => {
                                const estado =
                                  Boolean(
                                    asistencias[
                                      item._id
                                    ]?.asistio
                                  );

                                return (
                                  <tr
                                    key={
                                      item._id
                                    }
                                    className={
                                      estado
                                        ? "table-success"
                                        : ""
                                    }
                                  >
                                    <td>
                                      <div className="d-flex align-items-center gap-3">
                                        {(() => {
                                          const aprendiz =
                                            item.aprendizObjectId ||
                                            item.aprendiz ||
                                            {};

                                          const imagenAprendiz =
                                            aprendiz.imgAprendiz ||
                                            item.imgAprendiz ||
                                            "";

                                          return imagenAprendiz ? (
                                            <img
                                              src={imagenAprendiz}
                                              alt={item.nombreAprendiz || "Aprendiz"}
                                              className="rounded-circle border bg-white flex-shrink-0"
                                              style={{
                                                width: 44,
                                                height: 44,
                                                objectFit: "cover",
                                              }}
                                              onError={(event) => {
                                                event.currentTarget.onerror = null;
                                                event.currentTarget.src = FOTO_APRENDIZ_DEFAULT;
                                              }}
                                            />
                                          ) : (
                                            <img
                                              src={FOTO_APRENDIZ_DEFAULT}
                                              alt="Aprendiz sin fotografía"
                                              className="rounded-circle border bg-white flex-shrink-0"
                                              style={{
                                                width: 44,
                                                height: 44,
                                                objectFit: "cover",
                                              }}
                                            />
                                          );
                                        })()}

                                        <span className="fw-semibold">
                                          {item.nombreAprendiz}
                                        </span>
                                      </div>
                                    </td>

                                    <td className="text-center">
                                      <div className="form-check d-inline-flex align-items-center justify-content-center gap-2">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={
                                            estado
                                          }
                                          onChange={() =>
                                            handleCheckboxChange(
                                              item._id
                                            )
                                          }
                                          disabled={
                                            disabledAll ||
                                            tieneAsistencias ||
                                            !puedeCrear
                                          }
                                        />

                                        <span
                                          className={`badge ${
                                            estado
                                              ? "text-bg-success"
                                              : "text-bg-secondary"
                                          }`}
                                        >
                                          {estado
                                            ? "Presente"
                                            : "Ausente"}
                                        </span>
                                      </div>
                                    </td>

                                    <td>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={
                                          asistencias[
                                            item._id
                                          ]
                                            ?.justificacion ||
                                          ""
                                        }
                                        onChange={(
                                          evento
                                        ) =>
                                          handleJustificacionChange(
                                            item._id,
                                            evento
                                              .target
                                              .value
                                          )
                                        }
                                        placeholder="Observación o justificación"
                                        disabled={
                                          disabledAll ||
                                          !puedeModificarFormulario
                                        }
                                      />
                                    </td>
                                  </tr>
                                );
                              }
                            )
                          ) : (
                            <tr>
                              <td
                                colSpan="3"
                                className="text-center text-secondary py-4"
                              >
                                <i className="fa-solid fa-circle-info me-2" />
                                No hay aprendices
                                registrados
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Tarjetas para celular */}
                  <div className="d-md-none">
                    {aprendices.length > 0 ? (
                      aprendices.map((item) => {
                        const estado = Boolean(
                          asistencias[item._id]
                            ?.asistio
                        );

                        return (
                          <div
                            key={item._id}
                            className={`card mb-3 shadow-sm ${
                              estado
                                ? "border-success"
                                : ""
                            }`}
                          >
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <div className="d-flex align-items-center gap-3">
                                  {(() => {
                                    const aprendiz =
                                      item.aprendizObjectId ||
                                      item.aprendiz ||
                                      {};

                                    const imagenAprendiz =
                                      aprendiz.imgAprendiz ||
                                      item.imgAprendiz ||
                                      "";

                                    return (
                                      <img
                                        src={
                                          imagenAprendiz ||
                                          FOTO_APRENDIZ_DEFAULT
                                        }
                                        alt={
                                          item.nombreAprendiz ||
                                          "Aprendiz"
                                        }
                                        className="rounded-circle border bg-white flex-shrink-0"
                                        style={{
                                          width: 54,
                                          height: 54,
                                          objectFit: "cover",
                                        }}
                                        onError={(event) => {
                                          event.currentTarget.onerror = null;
                                          event.currentTarget.src =
                                            FOTO_APRENDIZ_DEFAULT;
                                        }}
                                      />
                                    );
                                  })()}

                                  <h6 className="mb-1 fw-semibold">
                                    {item.nombreAprendiz}
                                  </h6>
                                </div>

                                <span
                                  className={`badge ${
                                    estado
                                      ? "text-bg-success"
                                      : "text-bg-secondary"
                                  }`}
                                >
                                  {estado
                                    ? "Presente"
                                    : "Ausente"}
                                </span>
                              </div>

                              <div className="mt-3">
                                <div className="form-check">
                                  <input
                                    id={`asistencia-${item._id}`}
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={estado}
                                    onChange={() =>
                                      handleCheckboxChange(
                                        item._id
                                      )
                                    }
                                    disabled={
                                      disabledAll ||
                                      tieneAsistencias ||
                                      !puedeCrear
                                    }
                                  />

                                  <label
                                    className="form-check-label"
                                    htmlFor={`asistencia-${item._id}`}
                                  >
                                    Marcar asistencia
                                  </label>
                                </div>
                              </div>

                              <div className="mt-3">
                                <label
                                  htmlFor={`observacion-${item._id}`}
                                  className="form-label small text-secondary mb-1"
                                >
                                  Observaciones
                                </label>

                                <textarea
                                  id={`observacion-${item._id}`}
                                  className="form-control"
                                  rows="2"
                                  value={
                                    asistencias[
                                      item._id
                                    ]
                                      ?.justificacion ||
                                    ""
                                  }
                                  onChange={(
                                    evento
                                  ) =>
                                    handleJustificacionChange(
                                      item._id,
                                      evento
                                        .target.value
                                    )
                                  }
                                  placeholder="Observación o justificación"
                                  disabled={
                                    disabledAll ||
                                    !puedeModificarFormulario
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="alert alert-info text-center mb-0">
                        <i className="fa-solid fa-circle-info me-2" />
                        No hay aprendices
                        registrados
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarAsistencia;